import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import user from '../../helpers/user';
import { apiPaths } from '../../config/constants';
import { initialState } from '../defaultState';
import { ChannelsAndGenres, IliveTv } from '../../interfaces/StoreTypes';
import { getUserAllChannelsApi } from '../../services/serviceWrapper/ChannelsServiceWrapper';
import { IChannelEntity } from '@intigral/domain';
import channelsUtil from '../../helpers/channelsUtil';
import { getConfiguration } from '../../hooks/useConfiguration';
import { rethrowAsError } from '../../utils/asyncError';

type FetchChannelsResponseSuccess = {
    type: string;
    payload: ChannelsAndGenres;
};

interface IFetchChannelProps {
    data?: { channels: IChannelEntity; genres: any } | null;
    ppvEventConfig?: any;
    operators?: any;
}

export const fetchUserChannelsList = createAsyncThunk(
    'fetch/userchannels',
    async (payload: IFetchChannelProps, { getState }) => {
        try {
            let userChannelslist;
            if (payload?.data) {
                return { channels: payload.data?.channels, genres: payload.data?.genres };
            }

            //  FIXED: Get PPV config from React Query instead of Redux generalConfiguration
            // Note: In a Redux thunk, we can't use hooks, so we'll access the global query client
            const config = getConfiguration();
            const ppvEventConfig = config?.ppvEventConfig || [];

            if (user.getProfileMode() === 'kid') {
                // get kids channels
                userChannelslist = await getUserAllChannelsApi(apiPaths.DATA_KIDS_CHANNELS, ppvEventConfig);
            } else {
                userChannelslist = await getUserAllChannelsApi(apiPaths.DATA_CHANNELS, ppvEventConfig);
            }
            if (userChannelslist) {
                return userChannelslist;
            }
            return { channels: [], genres: [] };
        } catch (error) {
            rethrowAsError(error, 'Failed to fetch user channels');
        }
    }
);

export const storeLastChannelSelected = createAsyncThunk('livetv/storeLastChannel', async (channel: any) => {
    try {
        // Dispatch an action to update the store with the latest channel ID
        return channel;
    } catch (error) {
        rethrowAsError(error, 'Failed to store last channel');
    }
});

const liveTvSlice = createSlice({
    name: 'LiveTv',
    initialState: initialState.liveTv,
    reducers: {
        setUpdatedGenre: (state, action) => {
            const favoriteChannels = action.payload;
            const allChannels = JSON.parse(JSON.stringify(state.userChannels.channels));
            const updatedGenres = channelsUtil.compileLiveTVGenresWithCounts(allChannels, favoriteChannels);
            state.userChannels.genres = updatedGenres ?? [];
        },
    },
    extraReducers(builder) {
        builder.addCase(fetchUserChannelsList.pending, (state: IliveTv) => {
            state.isLoading = true;
            state.errMess = null;
            // state.userChannels = { channels: [], genres: [] };
        });
        builder.addCase(fetchUserChannelsList.fulfilled, (state: IliveTv, action: FetchChannelsResponseSuccess) => {
            state.userChannels = action.payload;
            state.isLoading = false;
        });
        builder.addCase(fetchUserChannelsList.rejected, (state: IliveTv, action: any) => {
            state.errMess = action.error.message;
            state.isLoading = false;
        });

        builder.addCase(storeLastChannelSelected.pending, (state: IliveTv, action) => {
            state.lastChannelSelected = {};
        });
        builder.addCase(storeLastChannelSelected.fulfilled, (state: IliveTv, action) => {
            state.lastChannelSelected = action.payload;
        });
        builder.addCase(storeLastChannelSelected.rejected, (state: IliveTv, action) => {
            state.lastChannelSelected = {};
        });
    },
});
export const { setUpdatedGenre } = liveTvSlice.actions;
export default liveTvSlice.reducer;
